// Global Instructions Rule Applied!
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../core/utils/endpoints.ts'
import { getAuthHeaders } from '../../core/utils/utilities.ts'
import { supabase } from '../../core/lib/supabase'
import { GlobalConfigsResponse } from './interfaces.ts'
import { validateGlobalConfigRecord } from '../../state_manager/data_models/global_config_record.ts'
import { setGlobalConfigs } from '../../state_manager/state_update_actions/global_configs/actions.ts'
import { store } from '../../state_manager/store.ts'
import { LookupHeadersModel } from '../../state_manager/data_models/lookup_headers_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

/**
 * Transform lookup headers into a format with detailsByCategory for easy lookup access
 */
export function transformLookupsForAuth(lookupHeaders: LookupHeadersModel[]): {
  detailsByCategory: Record<string, any[]>
} {
  const detailsByCategory: Record<string, any[]> = {}

  lookupHeaders.forEach((lookup) => {
    detailsByCategory[lookup.category] = lookup.details || []
  })

  return { detailsByCategory }
}

/**
 * Fetch all global configs from the API
 */
async function fetchAllGlobalConfigs(user: { id: string; email: string }): Promise<GlobalConfigsResponse> {
  try {
    const headers = await getAuthHeaders()
    headers['email'] = user.email
    headers['x-user-id'] = user.id

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.GLOBAL_CONFIGS.LIST), {
      method: API_ROUTES.GLOBAL_CONFIGS.LIST.method,
      headers
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch global configs: ${response.status} ${response.statusText}`)
    }

    const result = await response.json()
    const validatedData = validateGlobalConfigRecord(result.data || {})

    return {
      data: validatedData
    }
  } catch (error) {
    console.error('Error fetching global configs:', error)
    return {
      data: null,
      error: error instanceof Error ? error.message : 'Failed to fetch global configs'
    }
  }
}

/**
 * Persist a user preference for a menu item (e.g. selected fund for Fundraising Pipeline)
 */
export async function persistUserPreference(
  menuItemId: string,
  reportId: string | undefined,
  data: Record<string, unknown> = {}
): Promise<{ success: boolean; error?: string }> {
  try {
    const {
      data: { session }
    } = await supabase.auth.getSession()
    if (!session?.user?.id || !session?.user?.email) {
      return { success: false, error: 'Not authenticated' }
    }

    const headers = await getAuthHeaders()
    headers['email'] = session.user.email
    headers['x-user-id'] = session.user.id

    const body: Record<string, unknown> = { menu_item_id: menuItemId, data }
    if (reportId !== undefined && reportId !== '') {
      body.report_id = reportId
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.GLOBAL_CONFIGS.USER_PREFERENCES), {
      method: API_ROUTES.GLOBAL_CONFIGS.USER_PREFERENCES.method,
      headers,
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return {
        success: false,
        error: errorData.message || `Failed to persist preference: ${response.status}`
      }
    }
    return { success: true }
  } catch (error) {
    console.error('Error persisting user preference:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to persist preference'
    }
  }
}

/**
 * Upsert keys into the per-user global (non-report) preference blob.
 *
 * Separate endpoint from persistUserPreference: that one requires a
 * menu_item_id, which a preference global to the user (e.g. the left-nav
 * current area) does not have.
 *
 * THE CALLER PASSES ONLY ITS OWN KEYS; this function sends the whole blob.
 * The route is a PATCH in name only — it upserts `{user_id, data}` verbatim
 * (global_configs/router.py:203), so a partial body REPLACES every other key.
 * With one writer that never showed: the left-nav area was the only key, so
 * rewriting the blob with it was harmless. It bites the moment there are two —
 * a collapsed section wiped `current_area`, and the next navigation wiped the
 * collapsed positions straight back (owner, 2026-09-06; the DB had no
 * `agent_states_collapsed` row for any user despite the states strips shipping
 * that behaviour in August).
 *
 * Merging HERE rather than in the route keeps the fix to one function and one
 * layer. The Redux reducer already merges the same way
 * (SET_USER_PREFERENCES_APP_DATA), and callers dispatch before persisting, so
 * the store holds exactly the blob that should be stored.
 */
export async function persistAppPreference(
  data: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
  try {
    const {
      data: { session }
    } = await supabase.auth.getSession()
    if (!session?.user?.id || !session?.user?.email) {
      return { success: false, error: 'Not authenticated' }
    }

    const headers = await getAuthHeaders()
    headers['email'] = session.user.email
    headers['x-user-id'] = session.user.id

    const stored = ((store.getState() as any).globalConfigs?.user_preferences_app?.data ?? {}) as Record<string, unknown>
    const merged = { ...stored, ...data }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.GLOBAL_CONFIGS.USER_PREFERENCES_APP), {
      method: API_ROUTES.GLOBAL_CONFIGS.USER_PREFERENCES_APP.method,
      headers,
      body: JSON.stringify({ data: merged })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return {
        success: false,
        error: errorData.message || `Failed to persist app preference: ${response.status}`
      }
    }
    return { success: true }
  } catch (error) {
    console.error('Error persisting app preference:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to persist app preference'
    }
  }
}

/**
 * Delete the user preference row for a menu item (user + menu_item_id).
 */
export async function deleteUserPreference(menuItemId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const {
      data: { session }
    } = await supabase.auth.getSession()
    if (!session?.user?.id || !session?.user?.email) {
      return { success: false, error: 'Not authenticated' }
    }

    const headers = await getAuthHeaders()
    headers['email'] = session.user.email
    headers['x-user-id'] = session.user.id

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.GLOBAL_CONFIGS.USER_PREFERENCES_DELETE), {
      method: API_ROUTES.GLOBAL_CONFIGS.USER_PREFERENCES_DELETE.method,
      headers,
      body: JSON.stringify({ menu_item_id: menuItemId })
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return {
        success: false,
        error: errorData.message || `Failed to delete preference: ${response.status}`
      }
    }
    return { success: true }
  } catch (error) {
    console.error('Error deleting user preference:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete preference'
    }
  }
}

/**
 * Fetch global configs and dispatch actions to update state
 */
export const fetchGlobalConfigsThunk = (user: { id: string; email: string }) => async (dispatch: any) => {
  try {
    const response = await fetchAllGlobalConfigs(user)

    if (response.error || !response.data) {
      console.error('Error fetching global configs:', response.error || 'Failed to fetch global configs')
      return
    }

    const globalConfigs = response.data

    // Store the complete global configs record
    dispatch(setGlobalConfigs(globalConfigs))
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch global configs'
    console.error('Error fetching global configs:', errorMessage)
  }
}

/**
 * Ask the backend to reload its cached global configs from the database. Used
 * after a cfg write (e.g. pipeline age thresholds) so the change is visible
 * without a backend restart. The caller follows this with a re-fetch into Redux.
 */
export async function refreshGlobalConfigsCache(): Promise<{ success: boolean; error?: string }> {
  try {
    const headers = await getAuthHeaders()
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.GLOBAL_CONFIGS.REFRESH), {
      method: API_ROUTES.GLOBAL_CONFIGS.REFRESH.method,
      headers
    })
    if (!response.ok) {
      return { success: false, error: `Failed to refresh global configs: ${response.status} ${response.statusText}` }
    }
    return { success: true }
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Failed to refresh global configs' }
  }
}

export interface ConfigVersion {
  version: number
  changed_at: string | null
  poll_seconds: number
}

/**
 * Read the config-version counter: has any configuration changed, and how long
 * until the next check. ~300 bytes against the 459 kB the full global-configs
 * read costs, which is the whole point — poll this, re-pull only on a move.
 *
 * Serving it also syncs the backend's own cache, so a moved counter means the
 * subsequent GET /global-configs returns fresh data.
 *
 * Returns null on any failure. The caller treats that as "no information" and
 * simply tries again next tick — a blip must never trigger a re-pull.
 */
export async function fetchGlobalConfigsVersion(): Promise<ConfigVersion | null> {
  try {
    const headers = await getAuthHeaders()
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.GLOBAL_CONFIGS.VERSION), {
      method: API_ROUTES.GLOBAL_CONFIGS.VERSION.method,
      headers
    })
    if (!response.ok) return null
    const json = (await response.json()) as { data?: Partial<ConfigVersion> }
    const version = Number(json.data?.version)
    if (!Number.isFinite(version)) return null
    return {
      version,
      changed_at: json.data?.changed_at ?? null,
      poll_seconds: Number(json.data?.poll_seconds) || 30
    }
  } catch {
    return null
  }
}

/**
 * Reload the backend cache, then pull the refreshed global configs into Redux.
 * Call after a Configuration-screen cfg write so dependent screens (e.g. the
 * deal board's age-threshold colours) update without a restart.
 */
export const refreshGlobalConfigsThunk = (user: { id: string; email: string }) => async (dispatch: any) => {
  const refresh = await refreshGlobalConfigsCache()
  if (!refresh.success) {
    console.error('Error refreshing global configs cache:', refresh.error)
    // Fall through and still re-fetch — the cache may already be current.
  }
  await dispatch(fetchGlobalConfigsThunk(user))
}
